'use client';

import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';
import AppLogo from '@/components/ui/AppLogo';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClaimFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  hasDeal: 'yes' | 'not_yet' | '';
  estimatedEmdAmount: string;
  targetClosingDate: string;
}

interface ClaimResult {
  promoCode: string;
  email: string;
  isExisting: boolean;
}

interface UtmData {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  landingPage: string;
  referrer: string;
}

// ─── Minimal BPCON Header ─────────────────────────────────────────────────────

function BpconHeader() {
  return (
    <header className="bg-[#0D2B1F] border-b border-white/10">
      <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link href="/homepage" className="flex items-center gap-2.5">
          <AppLogo
            src="/assets/images/EB_logo_White_160_x_160_px_-1777167253747.png"
            size={36}
            className="rounded-md"
          />
          <span className="font-display font-semibold text-base tracking-tight text-white">
            EarnestBridge
          </span>
        </Link>
        <div className="text-right">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#52B788]">
            BPCON 2026
          </p>
          <p className="text-[10px] text-white/50 tracking-wide">Exhibitor • Booth E7</p>
        </div>
      </div>
    </header>
  );
}

// ─── Claim Form ───────────────────────────────────────────────────────────────

interface ClaimFormProps {
  onSuccess: (result: ClaimResult) => void;
  utmData: UtmData;
}

function ClaimForm({ onSuccess, utmData }: ClaimFormProps) {
  const [form, setForm] = useState<ClaimFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    hasDeal: '',
    estimatedEmdAmount: '',
    targetClosingDate: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ClaimFormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [started, setStarted] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      if (!started) {
        setStarted(true);
        trackEvent('bpcon26_claim_form_started');
      }
    },
    [started]
  );

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ClaimFormData, string>> = {};
    if (!form.firstName.trim()) newErrors.firstName = 'Required';
    if (!form.lastName.trim()) newErrors.lastName = 'Required';
    if (!form.email.trim()) {
      newErrors.email = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter a valid email';
    }
    if (!form.phone.trim()) newErrors.phone = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (submitting) return;

    setSubmitting(true);
    setSubmitError('');
    trackEvent('bpcon26_claim_submitted');

    try {
      const res = await fetch('/api/crm/bpcon26-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          company_name: form.company.trim() || null,
          active_deal: form.hasDeal === 'yes',
          estimated_emd_amount: form.estimatedEmdAmount || null,
          target_closing_date: form.targetClosingDate || null,
          landing_page: typeof window !== 'undefined' ? window.location.pathname : '/bpcon2026',
          referrer: typeof document !== 'undefined' ? document.referrer || null : null,
          utm_source: utmData.utmSource,
          utm_medium: utmData.utmMedium,
          utm_campaign: utmData.utmCampaign,
          utm_content: utmData.utmContent,
          utm_term: utmData.utmTerm,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setSubmitError(data.error ?? 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }

      trackEvent('bpcon26_claim_success', { promo_code: data.promoCode });
      onSuccess({ promoCode: data.promoCode, email: form.email.trim(), isExisting: data.isExistingPromo ?? data.isExisting ?? false });
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  const inputClass = (field: keyof ClaimFormData) =>
    `w-full px-4 py-3.5 rounded-lg border text-sm bg-white text-[#0D1F17] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#52B788] transition-all ${
      errors[field]
        ? 'border-red-400 focus:ring-red-300' :'border-[rgba(27,67,50,0.18)] focus:border-[#52B788]'
    }`;

  return (
    <div id="claim-form" className="bg-white rounded-2xl shadow-xl border border-[rgba(27,67,50,0.10)] p-6 md:p-8">
      <h2 className="font-display font-semibold text-xl text-[#0D1F17] mb-1">
        Claim Your $1,250 Savings
      </h2>
      <p className="text-sm text-[#6B7C74] mb-6">
        Complete the form and we&apos;ll immediately email you your personal BPCON26 promo code.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="firstName" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={form.firstName}
              onChange={handleChange}
              className={inputClass('firstName')}
              placeholder="Jane"
            />
            {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={form.lastName}
              onChange={handleChange}
              className={inputClass('lastName')}
              placeholder="Smith"
            />
            {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="email" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={form.email}
            onChange={handleChange}
            className={inputClass('email')}
            placeholder="jane@example.com"
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>

        <div className="mb-3">
          <label htmlFor="phone" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={form.phone}
            onChange={handleChange}
            className={inputClass('phone')}
            placeholder="(555) 000-0000"
          />
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="company" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
            Company <span className="text-[#9CA3AF] font-normal normal-case">(optional)</span>
          </label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            value={form.company}
            onChange={handleChange}
            className={inputClass('company')}
            placeholder="Acme Investments LLC"
          />
        </div>

        <div className="mb-4">
          <p className="text-xs font-semibold text-[#1B4332] mb-2 uppercase tracking-wide">
            Do you have a deal you&apos;re working on now?
          </p>
          <div className="flex gap-3">
            {(['yes', 'not_yet'] as const).map((val) => (
              <label
                key={val}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border cursor-pointer text-sm font-medium transition-all ${
                  form.hasDeal === val
                    ? 'border-[#1B4332] bg-[#1B4332] text-white'
                    : 'border-[rgba(27,67,50,0.18)] text-[#6B7C74] hover:border-[#52B788]'
                }`}
              >
                <input
                  type="radio"
                  name="hasDeal"
                  value={val}
                  checked={form.hasDeal === val}
                  onChange={handleChange}
                  className="sr-only"
                />
                {val === 'yes' ? 'Yes' : 'Not yet'}
              </label>
            ))}
          </div>
        </div>

        {form.hasDeal === 'yes' && (
          <div className="grid grid-cols-2 gap-3 mb-4 p-4 bg-[#F0EFE9] rounded-xl">
            <div>
              <label htmlFor="estimatedEmdAmount" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
                Est. EMD Amount
              </label>
              <input
                id="estimatedEmdAmount"
                name="estimatedEmdAmount"
                type="text"
                inputMode="numeric"
                value={form.estimatedEmdAmount}
                onChange={handleChange}
                className={inputClass('estimatedEmdAmount')}
                placeholder="$50,000"
              />
            </div>
            <div>
              <label htmlFor="targetClosingDate" className="block text-xs font-semibold text-[#1B4332] mb-1.5 uppercase tracking-wide">
                Target Close Date
              </label>
              <input
                id="targetClosingDate"
                name="targetClosingDate"
                type="date"
                value={form.targetClosingDate}
                onChange={handleChange}
                className={inputClass('targetClosingDate')}
              />
            </div>
          </div>
        )}

        {submitError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 px-6 bg-[#1B4332] hover:bg-[#0D2B1F] text-white font-bold text-sm uppercase tracking-[0.12em] rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#52B788] focus:ring-offset-2"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Claiming…
            </span>
          ) : (
            'Claim My $1,250 Savings'
          )}
        </button>

        <p className="text-xs text-[#9CA3AF] text-center mt-3 leading-relaxed">
          Your promo code never expires and may be used once on an eligible EarnestBridge transaction.{' '}
          <Link href="/privacy-policy" className="underline hover:text-[#52B788]">
            Privacy Policy
          </Link>
        </p>
      </form>
    </div>
  );
}

// ─── Success State ────────────────────────────────────────────────────────────

function SuccessState({ result }: { result: ClaimResult }) {
  const [copied, setCopied] = useState(false);
  const dealUrl = `https://earnestbridge.com/contact?promo_code=${encodeURIComponent(result.promoCode)}&utm_source=bpcon26&utm_medium=promo_claim&utm_campaign=bpcon26`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.promoCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback — clipboard not available
    }
  };

  useEffect(() => {
    trackEvent('bpcon26_promo_email_sent', { promo_code: result.promoCode });
  }, [result.promoCode]);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-[rgba(27,67,50,0.10)] p-6 md:p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-[#52B788]/15 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-[#52B788]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#52B788]">
            {result.isExisting ? 'Already Claimed' : 'Your BPCON26 Offer Is Claimed'}
          </p>
          <h2 className="font-display font-semibold text-lg text-[#0D1F17] leading-tight">
            You just saved $1,250.
          </h2>
        </div>
      </div>

      <div className="bg-[#0D2B1F] rounded-xl p-5 mb-5 text-center">
        <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#52B788] mb-2">
          Your Personal Promo Code
        </p>
        <p className="font-mono text-2xl font-bold text-white tracking-[0.12em] mb-4">
          {result.promoCode}
        </p>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 bg-[#52B788] hover:bg-[#74C69D] text-white text-xs font-bold uppercase tracking-[0.1em] px-5 py-2.5 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0D2B1F]"
        >
          {copied ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Code
            </>
          )}
        </button>
      </div>

      <div className="border border-[rgba(27,67,50,0.12)] rounded-xl overflow-hidden mb-5">
        <div className="flex justify-between items-center px-4 py-3 bg-[#F8F7F4] border-b border-[rgba(27,67,50,0.08)]">
          <span className="text-sm text-[#6B7C74]">Standard Admin Fee</span>
          <span className="text-sm text-[#6B7C74] line-through">$2,500</span>
        </div>
        <div className="flex justify-between items-center px-4 py-3 bg-[#F8F7F4] border-b border-[rgba(27,67,50,0.08)]">
          <span className="text-sm font-semibold text-[#1B4332]">Your Admin Fee</span>
          <span className="text-sm font-bold text-[#1B4332]">$1,250</span>
        </div>
        <div className="flex justify-between items-center px-4 py-3 bg-[#1B4332]">
          <span className="text-sm font-bold text-[#52B788]">Your Savings</span>
          <span className="text-sm font-bold text-[#52B788]">$1,250 — 50%</span>
        </div>
      </div>

      <p className="text-xs text-[#6B7C74] mb-4 leading-relaxed">
        Your promo code does not expire and can be used once on an eligible EarnestBridge transaction.
      </p>

      <div className="bg-[#F0EFE9] rounded-xl p-4 mb-5">
        <p className="text-xs text-[#6B7C74]">
          We&apos;ve also emailed this code to:{' '}
          <span className="font-semibold text-[#1B4332]">{result.email}</span>
        </p>
      </div>

      <div className="border border-[rgba(27,67,50,0.12)] rounded-xl p-5 text-center">
        <h3 className="font-display font-semibold text-base text-[#0D1F17] mb-1">
          Have a Deal Ready Now?
        </h3>
        <p className="text-xs text-[#6B7C74] mb-4">
          Put your promo to work. Tell us about your deal and see if EarnestBridge can help fund your EMD.
        </p>
        <Link
          href={dealUrl}
          onClick={() => trackEvent('bpcon26_deal_cta_clicked', { promo_code: result.promoCode })}
          className="inline-flex items-center gap-2 bg-[#1B4332] hover:bg-[#0D2B1F] text-white text-xs font-bold uppercase tracking-[0.1em] px-6 py-3 rounded-lg transition-all duration-200"
        >
          Submit My Deal →
        </Link>
      </div>
    </div>
  );
}

// ─── Static data ──────────────────────────────────────────────────────────────

const VALUE_PROPS = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: '72-Hour Average Funding Time',
    body: 'Move quickly when the transaction requires it.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Preserve Your Capital',
    body: 'Keep liquidity available for the rest of your pipeline.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'No Equity Dilution',
    body: 'Avoid giving away deal ownership simply to solve an EMD requirement.',
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'Simple Application',
    body: 'Keep the funding process streamlined.',
  },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Bring Us the Deal', body: "Tell EarnestBridge what you\'re working on." },
  { num: '02', title: 'Get Approved', body: 'Complete the EarnestBridge application and transaction review.' },
  { num: '03', title: 'We Fund the EMD', body: 'For an approved transaction, EarnestBridge funds the earnest money according to the program requirements.' },
  { num: '04', title: 'Keep Your Capital Moving', body: 'Keep more of your own capital available for the rest of your pipeline.' },
];

const INVESTOR_TYPES = [
  'Multifamily', 'Fix & Flip', 'BRRRR', 'Rental Acquisitions',
  'Commercial Real Estate', 'Industrial', 'Retail', 'Mixed-Use',
  'Self-Storage', 'Hospitality', 'Portfolio Acquisitions', 'Wholesale Transactions',
];

const FAQS = [
  {
    q: 'What is the BPCON26 promotion?',
    a: "It provides $1,250 off EarnestBridge's standard $2,500 Admin Fee on one eligible transaction.",
  },
  {
    q: 'Does the promo code expire?',
    a: 'No. Once claimed, the promo code does not expire.',
  },
  {
    q: 'How many times can I use it?',
    a: 'Once.',
  },
  {
    q: 'Do I have to be a new EarnestBridge customer?',
    a: 'No. Existing clients may also claim and use the promotion.',
  },
  {
    q: 'Do I need a deal now?',
    a: 'No. Claim your code now and use it when you have an eligible transaction.',
  },
  {
    q: 'Does claiming a promo guarantee financing?',
    a: 'No. All transactions remain subject to EarnestBridge eligibility, underwriting, documentation, and program requirements.',
  },
];

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[rgba(27,67,50,0.10)] last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-4 text-left gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#52B788] rounded"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-[#0D1F17]">{q}</span>
        <svg
          className={`w-4 h-4 text-[#52B788] flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <p className="pb-4 text-sm text-[#6B7C74] leading-relaxed">{a}</p>}
    </div>
  );
}

// ─── Inner page (uses useSearchParams — must be inside Suspense) ──────────────

function Bpcon2026Inner() {
  const searchParams = useSearchParams();
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);
  const [utmData, setUtmData] = useState<UtmData>({
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmContent: '',
    utmTerm: '',
    landingPage: '',
    referrer: '',
  });
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setUtmData({
      utmSource: searchParams?.get('utm_source') ?? '',
      utmMedium: searchParams?.get('utm_medium') ?? '',
      utmCampaign: searchParams?.get('utm_campaign') ?? '',
      utmContent: searchParams?.get('utm_content') ?? '',
      utmTerm: searchParams?.get('utm_term') ?? '',
      landingPage: typeof window !== 'undefined' ? window.location.href : '/bpcon2026',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
    trackEvent('bpcon26_landing_view');
  }, [searchParams]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4]">
      <BpconHeader />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="bg-[#0D2B1F] pt-12 pb-0 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(rgba(82,183,136,1) 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left: Hero copy */}
            <div className="pt-2 pb-10 md:pb-16">
              <div className="inline-flex items-center gap-2 bg-[#52B788]/15 border border-[#52B788]/30 rounded-full px-3.5 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#52B788] flex-shrink-0" />
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#52B788]">
                  BPCON 2026 Exclusive
                </span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.08] tracking-tight mb-3">
                MORE DEALS.
                <br />
                LESS CASH
                <br />
                TIED UP.
              </h1>
              <h2 className="text-lg md:text-xl font-semibold text-[#52B788] mb-5 leading-snug">
                Claim $1,250 Off Your EarnestBridge Admin Fee
              </h2>
              <p className="text-sm text-white/65 leading-relaxed mb-7 max-w-sm">
                Get 50% off EarnestBridge&apos;s standard $2,500 Admin Fee on one eligible deal. Claim your personal promo code today and use it whenever the right opportunity comes along.
              </p>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 max-w-xs">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                  <span className="text-xs text-white/50 uppercase tracking-wide">Standard Admin Fee</span>
                  <span className="text-sm text-white/40 line-through">$2,500</span>
                </div>
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                  <span className="text-xs text-white/70 uppercase tracking-wide">With Your BPCON26 Promo</span>
                  <span className="text-2xl font-bold text-white">$1,250</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#52B788] uppercase tracking-wide">You Save</span>
                  <span className="text-sm font-bold text-[#52B788]">$1,250 — 50%</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-white/45 mb-6">
                <span>No expiration</span>
                <span className="text-white/20">•</span>
                <span>One redemption per code</span>
              </div>
              <p className="text-xs font-semibold text-[#52B788] tracking-wide">
                Meet EarnestBridge at BPCON26 — Booth E7
              </p>
            </div>

            {/* Right: Claim form */}
            <div ref={formRef} className="md:pt-6 pb-8 md:pb-0 md:-mb-8">
              {claimResult ? (
                <SuccessState result={claimResult} />
              ) : (
                <ClaimForm onSuccess={setClaimResult} utmData={utmData} />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUE PROPOSITION ────────────────────────────────────────────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0D1F17] leading-tight mb-3">
              KEEP YOUR CAPITAL WORKING.
              <br />
              <span className="text-[#52B788]">NOT SITTING IN ESCROW.</span>
            </h2>
            <p className="text-sm text-[#6B7C74] max-w-xl mx-auto leading-relaxed">
              Earnest money deposits can tie up capital precisely when investors need liquidity for their next acquisition, renovation, operating expense, or opportunity. EarnestBridge provides EMD financing so investors can secure opportunities while preserving capital for the rest of their pipeline.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUE_PROPS.map((vp) => (
              <div
                key={vp.title}
                className="bg-[#F8F7F4] border border-[rgba(27,67,50,0.08)] rounded-xl p-5 hover:border-[#52B788]/40 transition-all duration-200"
              >
                <div className="w-9 h-9 rounded-lg bg-[#1B4332]/8 flex items-center justify-center text-[#1B4332] mb-3">
                  {vp.icon}
                </div>
                <h3 className="text-sm font-bold text-[#0D1F17] mb-1.5 leading-snug">{vp.title}</h3>
                <p className="text-xs text-[#6B7C74] leading-relaxed">{vp.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INVESTOR TYPES ───────────────────────────────────────────────── */}
      <section className="py-14 bg-[#F0EFE9]">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-display text-xl md:text-2xl font-bold text-[#0D1F17] text-center mb-8">
            Built for Investors Who Keep Moving.
          </h2>
          <div className="flex flex-wrap gap-2.5 justify-center">
            {INVESTOR_TYPES.map((type) => (
              <span
                key={type}
                className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-white border border-[rgba(27,67,50,0.12)] text-xs font-semibold text-[#1B4332] shadow-sm"
              >
                {type}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-[#9CA3AF] mt-6">
            Not all transaction types qualify. All transactions subject to EarnestBridge eligibility and program requirements.
          </p>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0D1F17] text-center mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step) => (
              <div key={step.num}>
                <div className="w-10 h-10 rounded-full bg-[#1B4332] flex items-center justify-center mb-4">
                  <span className="text-xs font-bold text-[#52B788]">{step.num}</span>
                </div>
                <h3 className="text-sm font-bold text-[#0D1F17] mb-2">{step.title}</h3>
                <p className="text-xs text-[#6B7C74] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECOND PROMO CTA ─────────────────────────────────────────────── */}
      <section className="py-14 bg-[#0D2B1F]">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#52B788] mb-3">
            BPCON26 EXCLUSIVE
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">
            SAVE $1,250
          </h2>
          <p className="text-sm text-white/60 mb-5">
            50% off the EarnestBridge Admin Fee on one eligible deal
          </p>
          <div className="flex items-center justify-center gap-6 mb-7">
            <div className="text-center">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Standard</p>
              <p className="text-xl text-white/40 line-through">$2,500</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#52B788] uppercase tracking-wide mb-1">With Promo</p>
              <p className="text-3xl font-bold text-white">$1,250</p>
            </div>
          </div>

          {claimResult ? (
            <div className="space-y-3">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 inline-block">
                <p className="text-xs text-white/50 mb-1">Your Code</p>
                <p className="font-mono text-lg font-bold text-white tracking-[0.1em]">{claimResult.promoCode}</p>
              </div>
              <div>
                <Link
                  href={`https://earnestbridge.com/contact?promo_code=${encodeURIComponent(claimResult.promoCode)}&utm_source=bpcon26&utm_medium=promo_claim&utm_campaign=bpcon26`}
                  onClick={() => trackEvent('bpcon26_deal_cta_clicked', { promo_code: claimResult.promoCode })}
                  className="inline-flex items-center gap-2 bg-[#52B788] hover:bg-[#74C69D] text-white text-sm font-bold uppercase tracking-[0.1em] px-8 py-3.5 rounded-xl transition-all duration-200"
                >
                  Submit My Deal →
                </Link>
              </div>
            </div>
          ) : (
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 bg-[#52B788] hover:bg-[#74C69D] text-white text-sm font-bold uppercase tracking-[0.1em] px-8 py-3.5 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0D2B1F]"
            >
              Claim My Promo Code
            </button>
          )}
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-16 bg-[#F8F7F4]">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-display text-2xl font-bold text-[#0D1F17] text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-2xl border border-[rgba(27,67,50,0.10)] shadow-sm px-6">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── MINIMAL FOOTER ───────────────────────────────────────────────── */}
      <footer className="bg-[#0D2B1F] py-10">
        <div className="max-w-5xl mx-auto px-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link href="/homepage" className="flex items-center gap-2.5">
              <AppLogo
                src="/assets/images/EB_logo_White_160_x_160_px_-1777167253747.png"
                size={30}
                className="rounded-md"
              />
              <span className="font-display font-semibold text-sm text-white">EarnestBridge</span>
            </Link>
            <div className="flex items-center gap-5 text-xs text-white/40">
              <Link href="/privacy-policy" className="hover:text-white/70 transition-colors">Privacy Policy</Link>
              <Link href="/terms" className="hover:text-white/70 transition-colors">Terms</Link>
              <Link href="/homepage" className="hover:text-white/70 transition-colors">Homepage</Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-[10px] text-white/30 leading-relaxed max-w-3xl">
              Promo provides a $1,250 discount against the standard $2,500 EarnestBridge Admin Fee on one eligible transaction. One redemption per issued promo code. Promo code has no cash value and does not expire. Claiming a promo code does not constitute financing approval. All transactions remain subject to EarnestBridge eligibility, underwriting, documentation and program requirements.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Default export — Suspense wrapper required for useSearchParams ───────────

export default function Bpcon2026Page() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#0D2B1F]" />}>
      <Bpcon2026Inner />
    </Suspense>
  );
}
